import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { ExtraWorkedTimeRequestStatus } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const provisionalExtraWorkedTimeRequestParamsSchema = z.object({
  "filter[shiftId]": z.string(),
  "filter[status][]": z.nativeEnum(ExtraWorkedTimeRequestStatus),
});

const provisionalExtraWorkedTimeRequestResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      attributes: z.object({
        start: z.string(),
        reason: z.string(),
      }),
    })
  ),
});

export type ProvisionalExtraWorkedTimeRequestResponse = z.infer<
  typeof provisionalExtraWorkedTimeRequestResponseSchema
>;

export function useGetProvisionalExtraWorkedTimeRequest(
  shiftId: string,
  options: UseGetQueryOptions<ProvisionalExtraWorkedTimeRequestResponse> = {}
): UseQueryResult<ProvisionalExtraWorkedTimeRequestResponse> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/extra-worked-time-requests`,
    requestSchema: provisionalExtraWorkedTimeRequestParamsSchema,
    responseSchema: provisionalExtraWorkedTimeRequestResponseSchema,
    queryParams: {
      "filter[shiftId]": shiftId,
      "filter[status][]": ExtraWorkedTimeRequestStatus.PROVISIONAL,
    },
    // A missing PROVISIONAL request is an expected state (no adjustment made yet).
    // Opt out of the dev-mode error boundary so a transient 502 doesn't crash the page.
    useErrorBoundary: false,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_EXTRA_WORKED_TIME_REQUESTS_FAILURE,
    },
    ...options,
  });
}
